export type CommentaryTone = 'normal' | 'excited' | 'warning' | 'achievement'

export type CommentaryEventKind =
  | 'level-start'
  | 'bonk'
  | 'hazard-death'
  | 'near-home'
  | 'coin-streak'
  | 'respawn'
  | 'win'
  | 'endless-milestone'

export interface CommentaryStats {
  levelIndex: number
  slides: number
  bonks: number
  coins: number
  deaths: number
  streak?: number
}

export interface CommentaryLine {
  text: string
  tone: CommentaryTone
  ttl: number
}

export interface CommentaryState {
  lastAt: number
  lastKind: CommentaryEventKind | null
  counts: Partial<Record<CommentaryEventKind, number>>
}

export const INITIAL_COMMENTARY_STATE: CommentaryState = { lastAt: 0, lastKind: null, counts: {} }

const LEVEL_START_LINES = [
  "Let's do this!",
  'Here we go again!',
  'Time for another challenge!',
  "You've got this!",
  "Show me what you've got!",
  'Ready when you are!',
  'New level, new adventure!',
  'We got this!',
  'Ready to roll!',
  "Let's make this one count!",
  'Another chance to walk into things!',
  "Let's see how creative your failures get!",
  'Your obstacles are waiting patiently!',
  'Time to turn left when you meant right!',
  'Another level, another opportunity for chaos!',
  "Let's see those legendary navigation skills!",
  'Your GPS has given up, but I believe in you!',
]

const BONK_LINES = [
  'Bonk!',
  'Boop!',
  'Oops!',
  'Watch it!',
  'Careful now!',
  'Bump!',
  'Whoops!',
  'Easy there!',
  'Mind the gap!',
  'Thunk!',
  'Oof!',
  'Nice collision!',
  'Smooth moves!',
  'Grace incarnate!',
  '10/10 landing!',
  'Physics lesson complete!',
  'The wall wins this round!',
  'Obstacle: 1, You: 0',
  'Try the door next time!',
  "That's not the way through!",
  'Solid as a rock... literally!',
]

const MANY_BONKS_LINES = [
  'Yo chill!',
  "Bruh! You're hitting everything!",
  "You really like hitting things, don't you?",
  "Whoa! You're a magnet for obstacles!",
  'Are you trying to collect all the collisions?',
  'You must really like those walls!',
  'Collision course: activated!',
  "Like, it's not going anywhere dude!",
  'Maybe try going around instead?',
  "Those obstacles aren't going to move!",
  'Houston, we have a navigation problem!',
  "GPS says: 'Recalculating... forever'",
  'The walls are winning this battle!',
  'Obstacle course champion right here!',
  'The scenic route involves fewer crashes!',
  'Bumper cars mode: ACTIVATED!',
  'Are we speed running collisions now?',
  'Those obstacles have families too!',
]

const HAZARD_LINES = [
  'Watch out for that lava!',
  'Lava is not your friend!',
  'Stop diving into the lava!',
  "That's hot! Literally!",
  "Lava surfing isn't a thing!",
  "You're on fire... literally!",
  'Maybe avoid the molten rock?',
  "That's some spicy terrain!",
  'Lava: 1, You: 0',
  'Swimming in lava is not recommended!',
  'Hot! Hot! Hot!',
  'The floor is literally lava!',
  "That's not a hot tub!",
  'Crispy player coming right up!',
  'Well done... literally!',
  "Fire safety 101: Don't walk in lava!",
]

const NEAR_HOME_LINES = [
  'Almost there!',
  'You can see the finish!',
  'Just a few more slides!',
  'The end is in sight!',
  'Home stretch!',
  'So close I can taste it!',
  'Victory is within reach!',
  "Don't give up now!",
  "You're practically there!",
  'Final push time!',
  'The portal awaits!',
  'You can smell victory!',
  'One final leap!',
  'The goal is right there!',
  'Finish strong!',
  "You've got this in the bag!",
  'Touch that portal!',
  'Make it count!',
  "Don't trip at the finish line!",
  'Stick the landing!',
]

const COIN_LINES = [
  'Cha-ching!',
  'Coin collector extraordinaire!',
  "You're making it rain coins!",
  'Treasure hunter status: achieved!',
  'Your piggy bank is getting heavy!',
  'Coin magnet in action!',
  'Show me the money!',
  'Golden touch activated!',
  "You're on a coin streak!",
  'Riches await!',
  'Midas would be jealous!',
  'Coin fever is real!',
  "You're swimming in gold!",
  'Ka-ching! Another one!',
  'Your pockets are jingling!',
]

const RESPAWN_LINES = [
  'Back on your feet!',
  'Round two. Ding ding!',
  'Shake it off, go again!',
  'The floor remembers nothing.',
  'One more try. Make it stylish.',
  'Home is still waiting for you.',
]

const WIN_LINES = [
  'HOME! You absolute legend!',
  'The window glows. Welcome home.',
  'Route complete. Grace approves.',
  'And THAT is how you slide!',
  'Ember is home safe. For now.',
]

const ENDLESS_LINES = [
  'The maze keeps growing. So do you.',
  'Another board conquered. Next!',
  'The endless dark blinks first.',
  'Streak rising. Keep sliding.',
  'The generator is getting nervous.',
]

const TABLES: Record<CommentaryEventKind, { lines: string[]; tone: CommentaryTone; ttl: number; debounceMs: number }> = {
  'level-start': { lines: LEVEL_START_LINES, tone: 'normal', ttl: 2200, debounceMs: 0 },
  bonk: { lines: BONK_LINES, tone: 'normal', ttl: 1500, debounceMs: 2000 },
  'hazard-death': { lines: HAZARD_LINES, tone: 'warning', ttl: 2600, debounceMs: 500 },
  'near-home': { lines: NEAR_HOME_LINES, tone: 'excited', ttl: 2000, debounceMs: 6000 },
  'coin-streak': { lines: COIN_LINES, tone: 'achievement', ttl: 2000, debounceMs: 5000 },
  respawn: { lines: RESPAWN_LINES, tone: 'normal', ttl: 2200, debounceMs: 500 },
  win: { lines: WIN_LINES, tone: 'achievement', ttl: 2600, debounceMs: 0 },
  'endless-milestone': { lines: ENDLESS_LINES, tone: 'excited', ttl: 2400, debounceMs: 0 },
}

function hash(n: number): number {
  let h = n | 0
  h = Math.imul(h ^ (h >>> 16), 2246822507)
  h = Math.imul(h ^ (h >>> 13), 3266489909)
  return (h ^= h >>> 16) >>> 0
}

export function pickCommentary(
  event: CommentaryEventKind,
  stats: CommentaryStats,
  state: CommentaryState,
  now: number,
): { line: CommentaryLine; state: CommentaryState } | null {
  const table = TABLES[event]
  if (!table) return null
  if (now - state.lastAt < table.debounceMs && state.lastKind === event) return null

  let lines = table.lines
  let tone = table.tone
  if (event === 'bonk' && stats.bonks > 8) {
    lines = MANY_BONKS_LINES
    tone = 'warning'
  }

  const seed = hash(
    stats.levelIndex * 7919 + stats.slides * 131 + stats.bonks * 17 + stats.coins * 5 + stats.deaths * 3 + (stats.streak ?? 0),
  )
  const text = lines[seed % lines.length]

  const counts = { ...state.counts, [event]: (state.counts[event] ?? 0) + 1 }
  return {
    line: { text, tone, ttl: table.ttl },
    state: { lastAt: now, lastKind: event, counts },
  }
}
